import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, ChevronDown } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { Motif } from "./Motif";
import { IMAGES } from "../../data/site";

const diplomas = [
  { title: "Bachelor Gezinswetenschappen", type: "Basisopleiding" },
  { title: "Therapieopleiding Interactionele Vormgeving (I.O.)", type: "Vierjarige therapieopleiding" },
];

const bijscholingen = [
  "Autisme & ADHD",
  "Nieuwe autoriteit & geweldloos verzet",
  "Sociaal-emotionele ontwikkeling",
  "Systemisch werken",
  "Veilige hechting & complex trauma bij kinderen",
];

const BijRow = ({ b, i, testid }) => (
  <li
    data-testid={testid}
    className="group flex items-baseline gap-4 py-3 md:py-4 border-b border-kern-taupe/20 last:border-b-0"
  >
    <span className="font-serif text-base md:text-lg text-kern-sage/70 w-6 md:w-8 shrink-0">
      {String(i + 1).padStart(2, "0")}
    </span>
    <span className="font-serif text-lg md:text-2xl text-kern-dark group-hover:text-kern-terracotta transition-colors duration-300">
      {b}
    </span>
  </li>
);

const Bijscholingen = ({ items }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 300, damping: 22 }}
      className="bg-white rounded-3xl shadow-[0_10px_30px_rgba(79,80,72,0.05)] overflow-hidden"
    >
      <button
        data-testid="bijscholingen-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-5 px-6 py-6 text-left"
        aria-expanded={open}
      >
        <span className="w-12 h-12 rounded-full bg-kern-olive flex items-center justify-center shrink-0">
          <GraduationCap size={20} className="text-kern-bg" strokeWidth={1.7} />
        </span>
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.2em] uppercase text-kern-terracotta mb-1">
            Permanente vorming
          </div>
          <div className="font-serif text-xl text-kern-dark leading-snug">
            Kortdurende opleidingen
          </div>
        </div>
        <ChevronDown
          size={20}
          className={`text-kern-taupe shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="bij"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <ul className="px-6 pb-5">
              {items.map((b, i) => (
                <BijRow key={b} b={b} i={i} testid={`bijscholing-${i}`} />
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const Over = () => (
  <section id="over" className="relative overflow-hidden bg-[#EFEBE1] py-16 md:py-32">
    <div className="pointer-events-none absolute -left-32 top-40 w-[460px] h-[460px] -z-0">
      <Motif opacity={0.2} />
    </div>

    {/* Intro */}
    <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <Reveal>
        <SectionLabel className="mb-6">Over mij</SectionLabel>
        <h2 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight text-kern-dark">
          Hallo, ik ben Griet Quirynen.
        </h2>
        <div className="mt-6 space-y-4 max-w-lg text-kern-taupe leading-relaxed">
          <p>
            Het gezicht achter De Kern en mama van twee fantastische zonen. Daarnaast
            werk ik als thuisbegeleidster en Gio-begeleidster bij Olo-rotonde.
          </p>
          <p>
            Al meer dan 20 jaar begeleid ik kinderen, jongeren en volwassenen in heel
            verschillende levensfases. Die ervaring leerde me dat achter elke hulpvraag
            een uniek verhaal schuilt. Daarom luister ik graag naar jullie verhaal,
            zonder oordeel en op jullie tempo.
          </p>
          <p>
            Ik geloof dat kleine inzichten een groot verschil kunnen maken. Ik help
            jullie graag om opnieuw rust, vertrouwen en verbinding te ervaren.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="relative">
          <div className="absolute -right-5 -top-5 w-44 h-44 rounded-full bg-kern-terracotta/20 -z-0" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-[48px] rounded-tr-[120px] aspect-[3/2] shadow-[0_30px_80px_rgba(79,80,72,0.14)]">
            <img
              src="/griet-over-v6.webp"
              alt="Griet Quirynen, gezinstherapeut bij Praktijk De Kern"
              loading="lazy"
              className="w-full h-full object-cover"
              style={{ objectPosition: "center" }}
            />
          </div>
        </div>
      </Reveal>
    </div>

    {/* Opleidingen */}
    <div className="relative max-w-7xl mx-auto px-5 md:px-8 mt-16 md:mt-32">
      <Reveal>
        <div className="relative overflow-hidden rounded-[48px] bg-kern-bg px-6 md:px-16 py-10 md:py-20 shadow-[0_20px_60px_rgba(79,80,72,0.06)]">
          <div className="pointer-events-none absolute -right-20 -bottom-24 w-[340px] h-[340px]">
            <Motif opacity={0.12} flip />
          </div>

          <div className="relative grid lg:grid-cols-5 gap-8 lg:gap-16">
            {/* Intro */}
            <div className="lg:col-span-2">
              <SectionLabel className="mb-6">Opleidingen</SectionLabel>
              <h3 className="font-serif text-2xl md:text-4xl leading-tight text-kern-dark">
                Ik blijf mezelf ontwikkelen.
              </h3>
              <p className="mt-5 text-kern-taupe leading-relaxed">
                Ik geloof dat leren nooit stopt. Daarom spijker ik mijn theoretische
                achtergrond regelmatig bij met vormingen binnen mijn vakgebied — zodat ik
                jou de best mogelijke begeleiding kan bieden.
              </p>
            </div>

            {/* Diploma's + bijscholingen */}
            <div className="lg:col-span-3">
              <div className="space-y-4">
                {diplomas.map((d, i) => (
                  <motion.div
                    key={d.title}
                    whileHover={{ y: -4 }}
                    transition={{ type: "spring", stiffness: 300, damping: 22 }}
                    data-testid={`diploma-${i}`}
                    className="flex items-center gap-5 bg-white rounded-3xl px-6 py-6 shadow-[0_10px_30px_rgba(79,80,72,0.05)]"
                  >
                    <span className="w-12 h-12 rounded-full bg-kern-olive flex items-center justify-center shrink-0">
                      <GraduationCap size={20} className="text-kern-bg" strokeWidth={1.7} />
                    </span>
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-kern-terracotta mb-1">
                        {d.type}
                      </div>
                      <div className="font-serif text-xl text-kern-dark leading-snug">
                        {d.title}
                      </div>
                    </div>
                  </motion.div>
                ))}
                <Bijscholingen items={bijscholingen} />
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>

    {/* Afsluiter */}
    <div className="relative max-w-7xl mx-auto px-5 md:px-8 mt-16 md:mt-32">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        <Reveal delay={0.1} className="order-2 lg:order-1">
          <div className="relative">
            <div className="absolute -left-5 -bottom-5 w-44 h-44 rounded-full bg-kern-sage/30 -z-0" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-[48px] rounded-bl-[160px] aspect-[4/5] shadow-[0_30px_80px_rgba(79,80,72,0.14)]">
              <img
                src={IMAGES.praktijk}
                alt="Sfeerbeeld van de praktijkruimte van De Kern"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </Reveal>

        <Reveal className="order-1 lg:order-2 max-w-xl">
          <p className="font-serif text-xl md:text-3xl leading-snug text-kern-dark italic">
            In mijn praktijk combineer ik professionele expertise met een warme,
            betrokken en persoonlijke aanpak. Ik kijk ernaar uit om jullie te ontmoeten.
          </p>
          <p className="mt-6 font-serif text-3xl text-kern-terracotta">Griet</p>
        </Reveal>
      </div>
    </div>
  </section>
);
